import dynamic from 'next/dynamic';

// UsageLoader transitively imports framer-motion. It only renders for users
// on the storage page (the only call site below is gated on isStoragePage),
// so dynamic-importing it keeps framer-motion out of the chat-page critical
// path. ssr:false because UsageLoader renders a client-only animated bar.
const UsageLoader = dynamic(() => import('@/components/auth/UsageLoader'), {
  ssr: false,
});
import { useAuthUser } from '@/hooks/useAuthUser';
import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import {
  selectLlmStreamingJustEnded,
  setLlmStreamingEnded,
} from '@/lib/redux/slices/chatSlice';
import type { UserUsageData } from '@/types/users-types';
import { formatStorageSizeMb } from '@/utils/formatStorageSize';
import { usePathname } from 'next/navigation';
import type React from 'react';
import { useEffect } from 'react';

interface SidebarUsageInfoProps {
  isUsageLoading: boolean;
  usageData: UserUsageData | null;
  usageError?: string | null;
  t: (key: string) => string;
  onRetry: () => void;
}

// Panel-footer usage indicator. Account, the menu and the upgrade CTA live in
// the primary rail (SidebarRail) — this component now only surfaces the
// contextual storage-usage bar on the storage page, so the always-visible
// context panel doesn't duplicate the rail's account zone.
export const SidebarUsageInfo: React.FC<SidebarUsageInfoProps> = ({
  usageData,
  t,
  onRetry,
}) => {
  const dispatch = useAppDispatch();
  const llmStreamJustEnded = useAppSelector(selectLlmStreamingJustEnded);
  const { userId } = useAuthUser();
  const pathname = usePathname();

  // Determine which page we're on (pathname includes locale prefix, use includes())
  const isStoragePage =
    pathname?.includes('/storage') || pathname?.includes('/ulozisko');
  // Storage data from the unified /users/usage API
  const storageData = usageData?.storage ?? null;

  // Refresh usage after a streamed response ends so the bar stays current.
  useEffect(() => {
    if (llmStreamJustEnded) {
      onRetry();
      dispatch(setLlmStreamingEnded(false));
    }
  }, [llmStreamJustEnded, dispatch, onRetry]);

  // Only the storage page has a contextual usage bar; everything else has no
  // panel footer (the account zone is in the rail).
  if (!userId || !isStoragePage || !storageData) {
    return null;
  }

  return (
    <div className='px-3 py-2 border-t border-line-default'>
      <UsageLoader
        consumed={storageData.used_mb}
        limit={storageData.max_mb}
        name={t('storageUsageTitle') || t('storage')}
        formatValue={formatStorageSizeMb}
        className='w-full'
      />
    </div>
  );
};
